import type { EligibilityCheckDoc } from '../types'

/**
 * Row-to-row relationship labels for Recent Checks (#289).
 *
 * The table is a ledger of check attempts, and a retry never adds a row — the
 * sweep re-issues on the SAME doc, so `retryCount` moves and the row stays one
 * row. What legitimately produces two rows for one patient-appointment is a
 * NEW check in the same flow: a DOS re-check of a provisional answer, a
 * correction re-run, or an SMS-recovery re-run. Those rows were rendering as
 * unrelated peers — same patient, same payer, same member id, two different
 * statuses, nothing tying them together — which is the "which row is the
 * truth?" the ticket describes.
 *
 * `flowId` is the correction-chain key: identical on every check of a chain,
 * so rows sharing a REAL one are by definition the same patient-appointment
 * (see `flowKey` for the docs that only look like they share one). Labels are
 * deliberately count-free ("a newer check", not "check 2 of 3") because the
 * table is server-paginated: siblings can sit on a page that isn't loaded, and
 * a count computed over the loaded rows would be a number that changes when
 * you press Load More. Supersession, unlike a count, is still true when part
 * of the chain is out of view.
 */
export interface FlowRelation {
  /** Compact chip rendered beside the patient name. */
  chip: string
  /** Full sentence for the chip's hover title. */
  title: string
}

/** The chain key, with the read layer's own fallback order for legacy docs. */
type FlowFields = Pick<EligibilityCheckDoc, 'id' | 'flowId' | 'eligibilityCheckSetId' | 'checkedAt'>

/**
 * The chain key — `null` for a row whose chain can't be proven, which then
 * gets no chip.
 *
 * A real `flowId` is the first check's own Firestore id, so rows carrying the
 * same one are by definition one patient-appointment. Docs that predate the
 * field don't have that: the backfill stamped `flowId := eligibilityCheckSetId`
 * and the read layer's `withFlowKey` stamps the same fallback for whatever the
 * backfill missed. A set id hashes doctor + policy + dob + name + clinic with
 * NO date in it, so one patient's annual exam and their follow-up six weeks
 * later hash to the same set — two independently billable visits, not a
 * superseded attempt. Calling one of those `Superseded` asserts a relationship
 * that does not exist, which is the exact failure the AC is written against,
 * so a row keyed only by its set is left unlabelled instead.
 *
 * The test is exact rather than heuristic: a set id is a 64-char sha-256 hex
 * digest and a check id is a 20-char Firestore auto id, so an id that equals
 * its own set id is a fallback key and can never be a real chain.
 */
function flowKey(check: FlowFields): string | null {
  if (!check.flowId) return null
  return check.flowId === check.eligibilityCheckSetId ? null : check.flowId
}

/**
 * Maps check id → relation, for the checks that HAVE a visible sibling. A
 * check whose chain contributes a single row to the list gets no entry (and
 * so no chip): there is no relationship to state. Nor does one whose chain
 * key is unprovable (see `flowKey`) — an unlabelled row says less than the
 * truth, a wrongly-labelled one says something untrue.
 */
export function buildFlowRelations(checks: FlowFields[]): Map<string, FlowRelation> {
  const byFlow = new Map<string, FlowFields[]>()
  for (const check of checks) {
    const key = flowKey(check)
    if (key === null) continue
    const group = byFlow.get(key)
    if (group) group.push(check)
    else byFlow.set(key, [check])
  }

  const relations = new Map<string, FlowRelation>()
  for (const group of byFlow.values()) {
    if (group.length < 2) continue
    // Newest by checkedAt; the id breaks ties so two checks written in the
    // same millisecond still get a stable, non-flickering assignment.
    const sorted = [...group].sort((a, b) => {
      const byTime = (b.checkedAt ?? '').localeCompare(a.checkedAt ?? '')
      return byTime !== 0 ? byTime : b.id.localeCompare(a.id)
    })
    const [newest, ...earlier] = sorted
    relations.set(newest.id, {
      chip: 'Latest',
      title: 'Latest check for this patient-appointment — an earlier check in the same chain is also in this list.',
    })
    for (const check of earlier) {
      relations.set(check.id, {
        chip: 'Superseded',
        title: 'Superseded — a newer check for this same patient-appointment is also in this list.',
      })
    }
  }
  return relations
}
